/**
 * NMS Store Launch · Hero
 * Countdown / live badge, data-driven links, reveal zoom, background video
 * (started after the intro, paused off-screen) and the full-film dialog.
 */
(() => {
  class NMSSlHero extends window.NMSBaseClass {
    constructor(element, service) {
      super();
      this.element = element;
      this.sl = service;
      this.labels = element.dataset;
      this.controller = new AbortController();
      this.muted = true;
      this.paused = false;
    }

    init() {
      super.init();
      const { signal } = this.controller;
      this.sl.fillLinks(this.element);
      this.#renderStatus();
      this.sl.on('tick', () => this.#renderStatus(), signal);

      this.sl.afterIntro('reveal', () => requestAnimationFrame(() => this.element.classList.add('is-revealed')), signal);
      if (this.refs.video) {
        this.sl.afterIntro('reveal', () => setTimeout(() => this.#startVideo(), 150), signal);
        this.refs.mute?.addEventListener('click', () => this.#toggleMute(), { signal });
        this.refs.pause?.addEventListener('click', () => this.#togglePause(), { signal });
      }

      this.refs.nearest?.addEventListener('click', () => {
        this.sl.nearMe();
        this.sl.scrollTo('stores');
      }, { signal });
      this.refs.visit?.addEventListener('click', () => this.sl.openVisit(), { signal });

      if (this.refs.film) {
        this.sl.film = { open: () => this.#openFilm() };
        this.refs.filmOpen?.addEventListener('click', () => this.#openFilm(), { signal });
        this.refs.filmClose?.addEventListener('click', () => this.refs.film.close(), { signal });
        this.refs.film.addEventListener('close', () => this.#closeFilm(), { signal });
      }
    }

    #renderStatus() {
      const { fill } = this.sl.helpers;
      const L = this.labels;
      let big = '';
      let label = '';
      if (this.sl.isLive()) {
        const status = this.sl.liveStatus();
        big = status.open ? L.labelOpen : status.opens.toUpperCase();
        label = status.open ? fill(L.labelUntil, { time: status.closes }) : L.labelOpensToday;
        if (this.refs.badgeLive) this.refs.badgeLive.textContent = fill(status.open ? L.badgeOpen : L.badgeClosed, { time: status.open ? status.closes : status.opens });
        this.refs.badgeDot?.style.setProperty('--dot', status.open ? '#3FA36B' : '');
      } else {
        const c = this.sl.countdown();
        big = !c.ok ? '' : c.ms > 0 ? (c.dd > 0 ? `${c.dd}d` : `${c.hh}h`) : L.labelOpen;
        label = !c.ok ? c.text : c.ms > 0 ? L.labelToOpening : L.labelNow;
      }
      if (this.refs.days) this.refs.days.textContent = big || ' ';
      if (this.refs.daysLabel) this.refs.daysLabel.textContent = label;
    }

    #startVideo() {
      const video = this.refs.video;
      if (!video || this.started) return;
      this.started = true;
      const mobile = video.dataset.srcMobile && window.matchMedia('(max-width: 759px)').matches;
      video.src = mobile ? video.dataset.srcMobile : video.dataset.src;
      video.addEventListener('playing', () => video.classList.add('is-playing'), { signal: this.controller.signal });
      // Only decode while the hero is on screen.
      this.io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !this.paused) this.#play();
        else video.pause();
      });
      this.io.observe(this.element);
    }

    #play() {
      const video = this.refs.video;
      video.muted = this.muted;
      video.play()?.catch(() => {});
    }

    #toggleMute() {
      this.muted = !this.muted;
      const video = this.refs.video;
      video.muted = this.muted;
      if (!this.started) this.#startVideo();
      this.refs.mute.setAttribute('aria-label', this.muted ? this.labels.labelUnmute : this.labels.labelMute);
      this.refs.muteIcon.innerHTML = this.sl.helpers.icon(this.muted ? 'muted' : 'sound');
    }

    #togglePause() {
      this.paused = !this.paused;
      if (this.paused) this.refs.video.pause();
      else this.#play();
      this.refs.pause.setAttribute('aria-label', this.paused ? this.labels.labelPlay : this.labels.labelPause);
      this.refs.pauseIcon.innerHTML = this.sl.helpers.icon(this.paused ? 'play' : 'pause');
    }

    #openFilm() {
      const film = this.refs.filmVideo;
      this.refs.video?.pause();
      if (!film.getAttribute('src')) film.src = film.dataset.src;
      this.refs.film.showModal();
      film.play()?.catch(() => {});
      this.sl.track('store_film_open');
    }

    #closeFilm() {
      this.refs.filmVideo.pause();
      if (this.started && !this.paused) this.#play();
    }

    destroy() {
      this.controller.abort();
      this.io?.disconnect();
      if (this.sl.film && this.refs.film) this.sl.film = null;
      super.destroy();
    }
  }

  const register = () => window.neemans.storeLaunch.register('[data-nms-sl-hero]', NMSSlHero);
  if (window.neemans?.storeLaunch) register();
  else document.addEventListener('nms-sl:ready', register, { once: true });
})();
